import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { NAV } from '@/constants/testIds';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@/components/ui/sheet';

const links = [
  { to: '/', label: 'Home', testId: NAV.home },
  { to: '/about', label: 'About', testId: NAV.about },
  { to: '/services', label: 'Services', testId: NAV.services },
  { to: '/industries', label: 'Industries', testId: NAV.industries },
  { to: '/why-choose-us', label: 'Why Us', testId: NAV.why },
  { to: '/insights', label: 'Insights', testId: NAV.blog },
];

const Logo = () => (
  <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2.5 group">
    <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-sm bg-tito-blue text-white font-display font-extrabold text-lg shadow-sm">
      T
      <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 bg-tito-gold rounded-sm" />
    </span>
    <div className="leading-tight">
      <div className="font-display font-bold text-[15px] text-slate-900 tracking-tight">TITO</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 -mt-0.5">Business Growth</div>
    </div>
  </Link>
);

export default function Navbar() {
  const [open, setOpen] = React.useState(false);

  return (
    <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              data-testid={l.testId}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm font-medium rounded-sm transition-colors ${
                  isActive
                    ? 'text-tito-blue'
                    : 'text-slate-600 hover:text-tito-blue'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/contact" data-testid={NAV.ctaButton}>
            <Button className="rounded-sm bg-tito-blue hover:bg-blue-900 text-white px-5 h-10 gap-1.5">
              Contact Us
              <ArrowUpRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>

        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid={NAV.mobileToggle}
                aria-label="Toggle menu"
              >
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white w-[300px]">
              <SheetTitle className="font-display text-lg">Navigation</SheetTitle>
              <div className="mt-6 flex flex-col gap-1">
                {links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === '/'}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-${l.testId}`}
                    className={({ isActive }) =>
                      `px-3 py-2.5 rounded-sm text-base font-medium ${
                        isActive
                          ? 'bg-blue-50 text-tito-blue'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <Link to="/contact" onClick={() => setOpen(false)} className="mt-4">
                  <Button className="w-full rounded-sm bg-tito-blue hover:bg-blue-900 text-white">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
